import { useMutation, useQueryClient } from '@tanstack/react-query';
import { privateApi } from '../../../api/api';

// Impor skema dan tipe yang relevan
import { UpdateUserFormData } from '../schemas/UpdateUserV4Schema';
import { UpdateUserV4Response, UpdateUserV4ResponseSchema } from '../schemas/GetUsersV4Schema';

const API_BASE_ENDPOINT = '/v4/user';

// Tipe untuk variabel yang diterima oleh fungsi mutasi
interface UpdateUserVariables {
    userId: string;
    formData: UpdateUserFormData;
}

/**
 * Hook kustom untuk memperbarui data pengguna (Versi 4).
 * Mengenkapsulasi logika mutasi, termasuk pemanggilan API,
 * validasi respons, dan invalidasi cache yang cerdas.
 * 
 * @returns Hasil dari React Query `useMutation`.
 */
export const useUpdateUserV4 = () => {
    const queryClient = useQueryClient();

    return useMutation<UpdateUserV4Response, Error, UpdateUserVariables>({
        /**
         * Fungsi mutasi yang akan dijalankan saat `mutate()` dipanggil.
         * @param variables - Objek yang berisi `userId` dan `formData`.
         */
        mutationFn: async ({ userId, formData }: UpdateUserVariables): Promise<UpdateUserV4Response> => {
            // Validasi sederhana untuk memastikan userId ada
            if (!userId) {
                throw new Error("User ID is required for an update operation.");
            }

            try {
                // Skema Zod di UpdateUserDialogV4 sudah menangani transformasi password kosong
                // menjadi `undefined`, sehingga tidak akan dikirim dalam payload jika tidak diisi.
                const response = await privateApi.put(`${API_BASE_ENDPOINT}/${userId}`, formData);

                // Validasi struktur data respons dari API.
                const validationResult = UpdateUserV4ResponseSchema.safeParse(response.data);

                if (!validationResult.success) {
                    console.error(`API response validation failed in useUpdateUserV4 for user ID ${userId}:`, validationResult.error.flatten());
                    throw new Error("Invalid data format received from the server after updating user.");
                }

                return validationResult.data;
            } catch (error) {
                console.error(`Failed to update user (V4) with ID ${userId}:`, error);
                throw error;
            }
        },

        /**
         * Callback yang dijalankan setelah mutasi berhasil.
         * Ini adalah tempat untuk invalidasi cache yang cerdas.
         * 
         * @param _data - Data yang dikembalikan dari `mutationFn`.
         * @param variables - Variabel yang dikirim ke `mutationFn`, berisi `userId`.
         */
        onSuccess: (_data, variables) => {
            const { userId } = variables;

            // 1. Invalidate semua query yang berhubungan dengan daftar pengguna.
            // Ini akan membuat tabel pengguna diperbarui.
            queryClient.invalidateQueries({ queryKey: ['users', 'v4', 'list'] });

            // 2. Invalidate query spesifik untuk detail pengguna yang baru saja diupdate.
            // Ini berguna jika ada halaman detail pengguna atau jika dialog update tetap terbuka
            // dan perlu menampilkan data yang paling baru.
            queryClient.invalidateQueries({ queryKey: ['users', 'v4', 'detail', userId] });
        },

        /**
         * Callback yang dijalankan jika mutasi gagal.
         */
        onError: (_error) => {
            // Logging sudah ditangani di mutationFn.
            // Blok ini bisa digunakan untuk side-effect global lainnya.
        },
    });
};